import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';

import { ChatApiService } from '../api/chat/chat.api.service';
import { ChatMessage, ChatState, ChatEvent, ChatEventType, ChatMessageEvent, ChatErrorEvent } from '../types/Chatbot';

const SESSION_STORAGE_KEY = 'vev-iq-chat-messages';
const MAX_MESSAGES = 100;

@Injectable({
  providedIn: 'root',
})
export class ChatService implements OnDestroy {
  private readonly stateSubject = new BehaviorSubject<ChatState>({
    messages: [],
    isConnected: false,
    isLoading: false,
    error: null,
    persistenceError: null,
    hasReachedLimit: false,
  });

  private currentMessageId: string | null = null;
  private streamSubscription: Subscription | null = null;
  private isProcessingMessage = false;

  public constructor(private readonly chatApiService: ChatApiService) {
    this.loadMessagesFromSession();
  }

  public get state$(): Observable<ChatState> {
    return this.stateSubject.asObservable();
  }

  public get currentState(): ChatState {
    return this.stateSubject.getValue();
  }

  public sendMessage(content: string): void {
    if (!content.trim() || this.isProcessingMessage || this.currentState.hasReachedLimit) {
      return;
    }

    this.isProcessingMessage = true;

    // Add user message
    const userMessage: ChatMessage = {
      id: uuidv4(),
      role: 'user',
      content: content.trim(),
      timestamp: new Date(),
    };

    this.updateState({
      messages: [...this.currentState.messages, userMessage],
      isLoading: true,
      error: null,
    });

    // Create placeholder for assistant response
    this.currentMessageId = uuidv4();
    const assistantMessage: ChatMessage = {
      id: this.currentMessageId,
      role: 'assistant',
      content: '',
      timestamp: new Date(),
      isStreaming: true,
    };

    this.updateState({
      messages: [...this.currentState.messages, assistantMessage],
    });

    this.saveMessagesToSession();

    // Connect to SSE
    this.streamSubscription = this.chatApiService.connect(content).subscribe({
      next: (event: ChatEvent) => this.handleEvent(event),
      error: (error) => this.handleError(error),
      complete: () => this.handleComplete(),
    });
  }

  public clearMessages(): void {
    this.disconnect();
    this.updateState({
      messages: [],
      error: null,
      hasReachedLimit: false,
    });
    sessionStorage.removeItem(SESSION_STORAGE_KEY);
  }

  public clearPersistenceError(): void {
    this.updateState({ persistenceError: null });
  }

  public disconnect(): void {
    if (this.streamSubscription) {
      this.streamSubscription.unsubscribe();
      this.streamSubscription = null;
    }
    this.chatApiService.disconnect();
    this.currentMessageId = null;
    this.isProcessingMessage = false;
    this.updateState({ isConnected: false, isLoading: false });
  }

  public ngOnDestroy(): void {
    this.disconnect();
  }

  private handleEvent(event: ChatEvent): void {
    switch (event.type) {
      case ChatEventType.Connected:
        this.updateState({ isConnected: true });
        break;

      case ChatEventType.Message:
        this.handleMessageEvent(event.data as ChatMessageEvent);
        break;

      case ChatEventType.Complete:
        this.handleCompleteEvent();
        break;

      case ChatEventType.Error:
        this.handleErrorEvent(event.data as ChatErrorEvent);
        break;

      case ChatEventType.Ping:
        // Keep-alive, no action needed
        break;
    }
  }

  private handleMessageEvent(data: ChatMessageEvent): void {
    const messages = this.currentState.messages.map((msg) => {
      if (msg.id === this.currentMessageId) {
        return {
          ...msg,
          content: msg.content + data.delta,
        };
      }
      return msg;
    });

    this.updateState({ messages });
    this.saveMessagesToSession();
  }

  private handleCompleteEvent(): void {
    const messages = this.currentState.messages.map((msg) => {
      if (msg.id === this.currentMessageId) {
        return {
          ...msg,
          isStreaming: false,
        };
      }
      return msg;
    });

    this.updateState({
      messages,
      isLoading: false,
    });

    this.currentMessageId = null;
    this.isProcessingMessage = false;
    this.saveMessagesToSession();
  }

  private handleErrorEvent(data: ChatErrorEvent): void {
    // Stop streaming and remove empty assistant message on error
    const messages = this.currentMessageId
      ? this.currentState.messages
          .filter((msg) => msg.id !== this.currentMessageId || msg.content.length > 0)
          .map((msg) => {
            if (msg.id === this.currentMessageId) {
              return { ...msg, isStreaming: false };
            }
            return msg;
          })
      : this.currentState.messages;

    this.updateState({
      messages,
      error: data.error,
      isLoading: false,
    });

    this.currentMessageId = null;
    this.isProcessingMessage = false;
    this.saveMessagesToSession();
  }

  private handleError(error: Error): void {
    // Stop streaming on connection error
    const messages = this.currentMessageId
      ? this.currentState.messages
          .filter((msg) => msg.id !== this.currentMessageId || msg.content.length > 0)
          .map((msg) => {
            if (msg.id === this.currentMessageId) {
              return { ...msg, isStreaming: false };
            }
            return msg;
          })
      : this.currentState.messages;

    this.updateState({
      messages,
      error: error.message || 'Connection failed',
      isLoading: false,
      isConnected: false,
    });

    this.currentMessageId = null;
    this.isProcessingMessage = false;
    this.saveMessagesToSession();
  }

  private handleComplete(): void {
    this.updateState({ isConnected: false });
  }

  private updateState(partial: Partial<ChatState>): void {
    const newState = {
      ...this.currentState,
      ...partial,
    };

    // Check if message limit has been reached
    if (newState.messages.length >= MAX_MESSAGES) {
      newState.hasReachedLimit = true;
    }

    this.stateSubject.next(newState);
  }

  /**
   * Saves messages to sessionStorage.
   * Note: ChatMessage.timestamp is a Date at runtime but serialized as ISO string for storage.
   * The loadMessagesFromSession method handles deserialization back to Date objects.
   */
  private saveMessagesToSession(): void {
    try {
      const messages = this.currentState.messages.map((msg) => ({
        ...msg,
        timestamp: msg.timestamp.toISOString(),
      }));
      sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(messages));
    } catch (e) {
      console.error('Failed to save messages to session storage:', e);
    }
  }

  /**
   * Loads messages from sessionStorage.
   * Deserializes ISO string timestamps back to Date objects.
   */
  private loadMessagesFromSession(): void {
    try {
      const stored = sessionStorage.getItem(SESSION_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (!Array.isArray(parsed)) {
          return;
        }
        const messages: ChatMessage[] = parsed
          .filter(this.isValidStoredMessage)
          .filter((msg) => msg.content?.length > 0) // Remove empty streaming messages
          .map((msg) => ({
            id: msg.id,
            role: msg.role,
            content: msg.content,
            timestamp: new Date(msg.timestamp),
            isStreaming: false, // Reset streaming state on reload
          }));
        this.updateState({ messages });
      }
    } catch (e) {
      console.error('Failed to load messages from session storage:', e);
      const error = e as Error;
      if (error instanceof SyntaxError) {
        sessionStorage.removeItem(SESSION_STORAGE_KEY);
      }
      this.updateState({ persistenceError: 'chatbot.persistence_error' });
    }
  }

  private isValidStoredMessage(
    msg: unknown,
  ): msg is { id: string; role: 'user' | 'assistant'; content: string; timestamp: string } {
    return (
      typeof msg === 'object' &&
      msg !== null &&
      typeof (msg as Record<string, unknown>).id === 'string' &&
      ((msg as Record<string, unknown>).role === 'user' || (msg as Record<string, unknown>).role === 'assistant') &&
      typeof (msg as Record<string, unknown>).content === 'string' &&
      typeof (msg as Record<string, unknown>).timestamp === 'string'
    );
  }
}
